import React, { useEffect, useState, useCallback } from 'react';
import { HiOutlineUsers, HiOutlineShieldCheck, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import AppLayout from '../../components/layout/AppLayout';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import StatCard from '../../components/ui/StatCard';
import FormInput from '../../components/forms/FormInput';
import FormSelect from '../../components/forms/FormSelect';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsers, createUser, updateUser, deleteUser, getRolePermissions, updateRolePermissions } from '../../api/auth';

const roleOptions = [
  { value: 'superadmin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'caretaker', label: 'Care Taker' },
  { value: 'gas_reader', label: 'Gas Reader' },
];

const roleBadge = {
  superadmin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  caretaker: 'bg-green-100 text-green-800',
  gas_reader: 'bg-orange-100 text-orange-800',
};

const roleLabel = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  caretaker: 'Care Taker',
  gas_reader: 'Gas Reader',
};

const featureLabels = {
  dashboard: 'Dashboard',
  contracts: 'Contracts (MOUs)',
  quotations: 'Quotations',
  services: 'Services',
  staff: 'Staff Allocation',
  security: 'Security',
  cleaning: 'Cleaning',
  water_treatment: 'Water Treatment',
  gas_readings: 'Gas Readings',
  members: 'Apartment Members',
};

const configurableRoles = ['admin', 'caretaker', 'gas_reader'];

const emptyForm = { name: '', email: '', password: '', role: 'caretaker' };

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Role permissions state
  const [permissions, setPermissions] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllUsers();
      setUsers(res.users || res.data || res || []);
    } catch (err) {
      console.error('Failed to load users', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    try {
      const res = await getRolePermissions();
      setPermissions(res.permissions || {});
    } catch (err) {
      console.error('Failed to load permissions', err);
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  useEffect(() => {
    if (activeTab === 'permissions') {
      fetchPermissions();
    }
  }, [activeTab, fetchPermissions]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({ name: user.name, email: user.email, password: '', role: user.role });
    setErrors({});
    setShowModal(true);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrors({});
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await updateUser(editing.id, payload);
      } else {
        await createUser(form);
      }
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors || {});
      } else {
        alert(err.response?.data?.message || 'Something went wrong');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const togglePermission = (role, feature) => {
    setPermissions((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        [feature]: !prev[role]?.[feature],
      },
    }));
  };

  const savePermissions = async (role) => {
    setSavingPermissions(true);
    try {
      await updateRolePermissions({ role, features: permissions[role] || {} });
      alert('Permissions saved successfully.');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const userList = Array.isArray(users) ? users : [];
  const adminCount = userList.filter((u) => u.role === 'admin').length;
  const caretakerCount = userList.filter((u) => u.role === 'caretaker').length;
  const gasReaderCount = userList.filter((u) => u.role === 'gas_reader').length;

  const isSuperAdmin = currentUser?.role === 'superadmin';

  return (
    <AppLayout title="User Management" breadcrumbs="Users">
      <div className="space-y-6">
        {/* Tabs */}
        {isSuperAdmin && (
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'users' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === 'permissions' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Role Permissions
            </button>
          </div>
        )}

        {activeTab === 'users' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard title="Total Users" value={userList.length} icon={HiOutlineUsers} color="bg-gray-100 text-gray-700" />
              <StatCard title="Admins" value={adminCount} icon={HiOutlineShieldCheck} color="bg-blue-100 text-blue-700" />
              <StatCard title="Care Takers" value={caretakerCount} icon={HiOutlineUsers} color="bg-green-100 text-green-700" />
              <StatCard title="Gas Readers" value={gasReaderCount} icon={HiOutlineUsers} color="bg-orange-100 text-orange-700" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">All Users</h2>
              <Button onClick={openCreate}>Add User</Button>
            </div>

            {/* Content */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading users...</div>
            ) : userList.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No users found</div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Role</th>
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userList.map((u) => (
                        <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">{u.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{u.email}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${roleBadge[u.role] || 'bg-gray-100 text-gray-800'}`}>
                              {roleLabel[u.role] || u.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => openEdit(u)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
                                <HiOutlinePencil className="w-4 h-4" />
                              </button>
                              {u.id !== currentUser?.id && (
                                <button onClick={() => setDeleteTarget(u)} className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600">
                                  <HiOutlineTrash className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden grid grid-cols-1 gap-4">
                  {userList.map((u) => (
                    <div key={u.id} className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{u.name}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">{u.email}</p>
                        </div>
                        <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${roleBadge[u.role] || 'bg-gray-100 text-gray-800'}`}>
                          {roleLabel[u.role] || u.role}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </p>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openEdit(u)}>Edit</Button>
                        {u.id !== currentUser?.id && (
                          <Button variant="danger" size="sm" onClick={() => setDeleteTarget(u)}>Delete</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {activeTab === 'permissions' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Role Permissions</h2>
              <p className="text-sm text-gray-500 mt-1">Configure which sidebar menu items each role can access.</p>
            </div>

            {permissionsLoading ? (
              <div className="text-center py-12 text-gray-500">Loading permissions...</div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50">
                        <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Feature</th>
                        {configurableRoles.map((role) => (
                          <th key={role} className="text-center px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                            {roleLabel[role]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(featureLabels).map(([feature, label]) => (
                        <tr key={feature} className="border-b border-gray-50">
                          <td className="px-6 py-3 text-sm font-medium text-gray-900">{label}</td>
                          {configurableRoles.map((role) => (
                            <td key={role} className="px-6 py-3 text-center">
                              <button
                                onClick={() => togglePermission(role, feature)}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                  permissions[role]?.[feature] ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                    permissions[role]?.[feature] ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                  {configurableRoles.map((role) => (
                    <Button
                      key={role}
                      onClick={() => savePermissions(role)}
                      loading={savingPermissions}
                      variant="secondary"
                      size="sm"
                    >
                      Save {roleLabel[role]}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete User" size="sm">
        <div className="text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
            <HiOutlineTrash className="w-7 h-7 text-red-500" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Are you sure you want to delete</p>
            <p className="font-semibold text-gray-900 mt-1">{deleteTarget?.name}</p>
            <p className="text-xs text-gray-400 mt-1">This action cannot be undone.</p>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete}>Delete</Button>
          </div>
        </div>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editing ? 'Edit User' : 'Add User'} size="md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <FormInput label="Name" name="name" value={form.name} onChange={handleChange} error={errors.name?.[0]} required />
          <FormInput label="Email" name="email" type="email" value={form.email} onChange={handleChange} error={errors.email?.[0]} required />
          <FormInput
            label={editing ? 'Password (leave blank to keep current)' : 'Password'}
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            error={errors.password?.[0]}
            required={!editing}
          />
          <FormSelect label="Role" name="role" value={form.role} onChange={handleChange} options={roleOptions} error={errors.role?.[0]} required />
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" type="button" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editing ? 'Update User' : 'Add User'}</Button>
          </div>
        </form>
      </Modal>
    </AppLayout>
  );
}
